import { AlertCircle } from 'lucide-react';
import { forwardRef, useId, type InputHTMLAttributes, type ReactNode } from 'react';
import styles from './FloatingField.module.css';

// A text field whose label starts inside the box and lifts onto the border once
// the field is focused or filled.
//
// It is one component rather than a Field wrapping an Input because the label is
// part of the control here, not a thing sitting above it: it has to follow the
// input in the DOM for the sibling selector to see the input's state, and it has
// to be positioned over it. Ours, never a bare browser control (CLAUDE.md §5).
//
// Best on a short, self-explanatory form — sign in, a reset, an invite. A dense
// data form still wants Field, where the labels stay put and stay scannable.

type NativeInputProps = Omit<InputHTMLAttributes<HTMLInputElement>, 'className' | 'size'>;

interface FloatingFieldProps extends NativeInputProps {
  readonly label: string;
  readonly hint?: string;
  readonly error?: string;
  readonly isRequired?: boolean;
  /** Pinned inside the control on the right — show/hide on a password. */
  readonly action?: ReactNode;
}

export const FloatingField = forwardRef<HTMLInputElement, FloatingFieldProps>(
  function FloatingField(
    { label, hint, error, isRequired = false, action, id, placeholder, ...rest },
    ref,
  ): ReactNode {
    const generatedId = useId();
    const inputId = id ?? generatedId;
    const hintId = `${inputId}-hint`;
    const errorId = `${inputId}-error`;

    const describedBy =
      [hint === undefined ? '' : hintId, error === undefined ? '' : errorId]
        .filter(Boolean)
        .join(' ') || undefined;

    return (
      <div className={styles.field}>
        <div className={styles.group}>
          <input
            {...rest}
            ref={ref}
            id={inputId}
            // A blank placeholder rather than none: `:placeholder-shown` is what
            // tells the label whether the field is empty, and with no placeholder
            // attribute at all it never matches and the label never comes back down.
            placeholder={placeholder ?? ' '}
            className={`${styles.input} ${action === undefined ? '' : styles.withAction}`}
            aria-invalid={error === undefined ? undefined : true}
            aria-describedby={describedBy}
          />

          <label className={styles.label} htmlFor={inputId}>
            {label}
            {isRequired && (
              <span className={styles.required} aria-label="required">
                {' '}
                *
              </span>
            )}
          </label>

          {action !== undefined && <span className={styles.action}>{action}</span>}
        </div>

        {hint !== undefined && error === undefined && (
          <span id={hintId} className={styles.hint}>
            {hint}
          </span>
        )}
        {error !== undefined && (
          <span id={errorId} className={styles.error} role="alert">
            <AlertCircle size={13} aria-hidden />
            {error}
          </span>
        )}
      </div>
    );
  },
);
